/*
 * Addressing for Esri's v2 basemap styles service, which is where the label,
 * road and boundary content lives. The imagery host (ibasemaps-api) serves only
 * World_Imagery and Elevation/World_Hillshade — no reference layers at all — so
 * a hybrid base map has to combine the two services.
 *
 * The style takes `language` and `worldview`, which between them replace the
 * tenant-country biasing the Google base map used to do, and go further: labels
 * follow the signed-in user's locale rather than only the tenant's country.
 */

const STYLES_HOST =
  'https://basemapstyles-api.arcgis.com/arcgis/rest/services/styles/v2/styles'

const LABELS_STYLE = 'arcgis/imagery/labels'

/*
 * supportedLanguageCodes for arcgis/imagery, read from the service's own
 * styles/self document. Kept as the literal strings Esri returns, casing
 * included — the service matches them exactly.
 */
const ESRI_LANGUAGES = [
  'ar',
  'bs',
  'bg',
  'ca',
  'zh-HK',
  'zh-CN',
  'zh-TW',
  'hr',
  'cs',
  'da',
  'nl',
  'en',
  'et',
  'fi',
  'fr',
  'de',
  'el',
  'he',
  'hu',
  'id',
  'it',
  'ja',
  'ko',
  'lv',
  'lt',
  'nb',
  'no',
  'pl',
  'pt-BR',
  'pt-PT',
  'ro',
  'ru',
  'sr',
  'sk',
  'sl',
  'es',
  'sv',
  'th',
  'tr',
  'uk',
  'vi',
]

/*
 * Esri's own fallback: every name in its local script. Better than forcing
 * English on the locales it has no translations for — this app ships Azerbaijani
 * and Kazakh, neither of which the styles service supports.
 */
const DEFAULT_LANGUAGE = 'global'

const LANGUAGES_BY_TAG = new Map(
  ESRI_LANGUAGES.map((code) => [code.toLowerCase(), code]),
)

/*
 * The ten worldviews the service publishes, keyed by the ISO 3166-1 alpha-2 code
 * that tenant records carry. A country outside this list has no worldview of its
 * own; the parameter is then left off and Esri renders its default.
 */
const WORLDVIEWS_BY_COUNTRY: Record<string, string> = {
  ae: 'unitedArabEmirates',
  cn: 'china',
  il: 'israel',
  in: 'india',
  jp: 'japan',
  kr: 'southKorea',
  ma: 'morocco',
  pk: 'pakistan',
  us: 'unitedStatesOfAmerica',
  vn: 'vietnam',
}

/**
 * Maps an i18n locale onto a language the styles service understands. Regional
 * tags resolve to the exact code when Esri publishes one (pt-BR) and otherwise
 * fall back to the base language (es-AR -> es, fr-CA -> fr).
 */
export const toEsriLanguage = (locale?: string | null): string => {
  const tag = String(locale ?? '')
    .trim()
    .toLowerCase()
  if (!tag) return DEFAULT_LANGUAGE

  return (
    LANGUAGES_BY_TAG.get(tag) ??
    LANGUAGES_BY_TAG.get(tag.split('-')[0]) ??
    DEFAULT_LANGUAGE
  )
}

/**
 * Maps a tenant's country onto an Esri worldview, or undefined when Esri does
 * not publish one for it. Tenant records have carried both cases of the ISO code
 * over the years, so the comparison is case-insensitive.
 */
export const toEsriWorldview = (
  countryId?: string | null,
): string | undefined =>
  WORLDVIEWS_BY_COUNTRY[
    String(countryId ?? '')
      .trim()
      .toLowerCase()
  ]

export type LabelsStyleOptions = {
  /** i18n locale of the signed-in user, e.g. 'pt-BR'. */
  locale?: string | null
  /** ISO 3166-1 alpha-2 country of the tenant, e.g. 'in'. */
  countryId?: string | null
}

/**
 * URL of the labels/roads/boundaries style, carrying the basemap session token.
 * Requires a key with Basemaps privileges behind that session; an imagery-only
 * key gets a 498 from this host.
 */
export const labelsStyleUrl = (
  token: string,
  { locale, countryId }: LabelsStyleOptions = {},
): string => {
  const params = new URLSearchParams({
    token,
    language: toEsriLanguage(locale),
  })

  const worldview = toEsriWorldview(countryId)
  if (worldview) params.set('worldview', worldview)

  return `${STYLES_HOST}/${LABELS_STYLE}?${params}`
}

/*
 * Hosts whose requests must carry a token. The style document Esri returns
 * is only partly self-sufficient: its `glyphs` URL already has a token embedded
 * and its `sprite` lives on the public CDN, but the vector tile service — both
 * the TileJSON and every .pbf tile — is rejected without one. Requests to
 * anything else (the sprite CDN included) are left untouched.
 */
const TOKENED_HOSTS = new Set([
  'basemaps-api.arcgis.com',
  'ibasemaps-api.arcgis.com',
])

/**
 * Adds the basemap session token to an ArcGIS request that needs one and has not
 * already got one. Passed to ol-mapbox-style as its `transformRequest`, which
 * sees every sub-resource the style pulls in.
 */
export const withArcGISToken = (url: string, token: string): string => {
  let parsed: URL
  try {
    parsed = new URL(url)
  } catch {
    return url
  }

  if (!TOKENED_HOSTS.has(parsed.hostname)) return url
  if (parsed.searchParams.has('token')) return url

  parsed.searchParams.set('token', token)
  return parsed.toString()
}
